module Client.Forms.TalgilTriggerForm

open System
open Client.Components
open Client.Components.Select
open Client.DomainTypes.Msg
open Client.Infrastructure.Api
open Elmish
open Fable.FontAwesome
open Fable.React
open Fable.React.Props
open Fulma
open Shared
open Shared.Dto
open Shared.Dto.Dto
open Shared.Dto.Form
open Shared.Infrastructure


type LoadableControllerInfo =
    | NotLoaded
    | Loading
    | Loaded of TalgilTriggerForm.ControllerInfoDto
    | Error of string

type Model = {
    Session: UserSession
    AvailableSensors: IdValue<MapSensorDto> list
    ControllerId: string option
    LoadDataRequestRunning: bool
    ControllerInfo: LoadableControllerInfo
    SelectedProgram: TalgilTriggerForm.ProgramDto option
    SelectedSensor: IdValue<MapSensorDto> option
    IsActive: bool
    CreateRequestRunning: bool
}

let private controllerName (info: LoadableControllerInfo) : string option =
    match info with
    | Loaded info -> Some info.Name
    | _ -> None

let init session sensors = {
    Session = session
    AvailableSensors = sensors
    ControllerId = None
    LoadDataRequestRunning = false
    ControllerInfo = NotLoaded
    SelectedProgram = None
    SelectedSensor = List.tryHead sensors
    IsActive = true
    CreateRequestRunning = false
}

let update (msg: TalgilTriggerFormMsg) (model: Model) =
    match msg with
    | TalgilTriggerFormMsg.ControllerIdChanged controllerId ->
        let newModel = {
            model with
                ControllerId = controllerId
                ControllerInfo = NotLoaded
        }

        newModel, Cmd.none
    | TalgilTriggerFormMsg.LoadControllerInfo id ->
        let requestData = {
            SessionKey = model.Session.SessionKey
            Data = id
        }

        let cmd =
            Cmd.OfAsync.perform api.getTalgilControllerInfo requestData TalgilTriggerFormMsg.ControllerInfoLoaded

        { model with LoadDataRequestRunning = true }, cmd
    | TalgilTriggerFormMsg.ControllerInfoLoaded response ->
        let newModel =
            match response with
            | Result.Ok info -> {
                model with
                    ControllerInfo = Loaded info
                    SelectedProgram = List.tryHead info.Programs
              }
            | Result.Error error -> {
                model with
                    ControllerInfo = AuthenticatedResponseError.toString error |> LoadableControllerInfo.Error
                    SelectedProgram = None
              }

        { newModel with LoadDataRequestRunning = false }, Cmd.none
    | TalgilTriggerFormMsg.ProgramSelected program -> { model with SelectedProgram = program }, Cmd.none
    | SensorSelected sensor -> { model with SelectedSensor = sensor }, Cmd.none
    | CreateTrigger newTriggerDto ->
        let requestData = {
            SessionKey = model.Session.SessionKey
            Data = newTriggerDto
        }

        let cmd = Cmd.OfAsync.perform api.createTalgilTrigger requestData TriggerCreated

        { model with CreateRequestRunning = true }, cmd
    | TalgilTriggerFormMsg.IsActiveChanged isActive -> { model with IsActive = isActive }, Cmd.none
    | TriggerCreated _ -> raise (Exception "That part should not be called")


let private controllerIdInput (model: Model) dispatch =
    Field.div [] [
        Label.label [] [ str "Controller ID" ]
        Control.div [] [
            Input.text [
                model.ControllerId |> Option.defaultValue "" |> Input.Value
                Input.OnChange(fun event ->
                    event.Value
                    |> String.toOption
                    |> TalgilTriggerFormMsg.ControllerIdChanged
                    |> dispatch
                )
            ]
        ]
    ]

let private controllerNameInput (model: Model) =
    Field.div [] [
        Label.label [] [ str "Controller Name" ]
        Control.div [] [
            Input.text [
                model.ControllerId |> Option.defaultValue "" |> Input.Value

                Input.Disabled true
            ]
        ]
    ]

let private isActiveCheckbox dispatch (model: Model) =
    Field.div [] [
        Control.div [] [
            Checkbox.checkbox [] [
                Checkbox.input [
                    Props [
                        Checked model.IsActive
                        OnChange(fun _ -> not model.IsActive |> TalgilTriggerFormMsg.IsActiveChanged |> dispatch)
                    ]
                ]

                str "Soll der Trigger aktiviert werden?"
            ]
        ]
    ]

let private sensorSelect dispatch (model: Model) =
    Field.div [] [
        Control.div [] [
            Label.label [] [ str "Bodensensor" ]
            MapSensorSelect.view
                (TalgilTriggerFormMsg.SensorSelected >> dispatch)
                model.SelectedSensor
                model.AvailableSensors
                false
        ]
    ]

let private loadControllerInfoButton dispatch (isLoading: bool) (controllerId: string option) =
    let buttonOptions =
        match controllerId with
        | Some id -> [
            CommonButtons.onClick (fun _ -> id |> int |> TalgilTriggerFormMsg.LoadControllerInfo |> dispatch)
          ]
        | None -> [ Button.Disabled true ]

    Button.button [
        Button.Color IsLink
        Button.IsLoading isLoading

        yield! buttonOptions
    ] [
        Icon.createWithText Fa.Solid.Download "Lade Informationen vom Controller"
    ]

let private programSelectView
    dispatch
    (availablePrograms: TalgilTriggerForm.ProgramDto list)
    (selectedProgram: TalgilTriggerForm.ProgramDto option)
    =
    Field.div [] [
        Label.label [] [ str "Talgil Programm" ]
        Control.div [] [
            TalgilProgramSelect.view
                (TalgilTriggerFormMsg.ProgramSelected >> dispatch)
                selectedProgram
                availablePrograms
        ]
    ]


let private controllerInfoView
    dispatch
    (selectedProgram: TalgilTriggerForm.ProgramDto option)
    (controllerInfo: TalgilTriggerForm.ControllerInfoDto)
    =
    [
        Field.div [] [
            Label.label [] [ str "Controller Name" ]
            Control.div [] [
                Input.text [
                    Input.Value controllerInfo.Name
                    Input.Disabled true
                ]
            ]
        ]

        programSelectView dispatch controllerInfo.Programs selectedProgram
    ]

let private loadableControllerInfoView dispatch (model: Model) =
    match model.ControllerInfo with
    | NotLoaded -> [
        Field.div [] [
            Label.label [] []
            Control.div [] [
                loadControllerInfoButton dispatch model.LoadDataRequestRunning model.ControllerId
            ]
        ]
      ]
    | Error error -> [
        Field.div [] [
            Label.label [] []
            Control.div [] [
                loadControllerInfoButton dispatch false model.ControllerId
            ]
        ]

        Notification.notification [ Notification.Color IsDanger ] [ str error ]
      ]
    | Loading -> [
        Field.div [] [
            Label.label [] []
            Control.div [] [
                loadControllerInfoButton dispatch true model.ControllerId
            ]
        ]
      ]
    | Loaded info -> controllerInfoView dispatch model.SelectedProgram info

let priceInfoField =
    Field.div [] [
        Label.label [] [ str "Preis" ]
        Control.div [] [
            p [] [
                str "Diese Funktion befindet sich aktuell in der Testphase und ist derzeit kostenlos"
            ]
        ]
    ]

let view dispatch model =
    form [] [
        sensorSelect dispatch model

        isActiveCheckbox dispatch model

        controllerIdInput model dispatch

        yield! loadableControllerInfoView dispatch model

        priceInfoField
    ]

let private createNewTriggerData (model: Model) : TalgilTriggerForm.NewTriggerDto option =
    Some TalgilTriggerForm.newTriggerDto
    |> Option.apply (Option.map int model.ControllerId)
    |> Option.apply (controllerName model.ControllerInfo)
    |> Option.apply model.SelectedProgram
    |> Option.apply (Option.map (fun sensor -> sensor.Id) model.SelectedSensor)
    |> Option.apply (Some model.IsActive)

let buttonView dispatch (model: Model) =
    let newTriggerData = createNewTriggerData model

    [
        Button.button [
            Button.Color IsSuccess
            Button.IsLoading model.CreateRequestRunning

            match newTriggerData with
            | Some data -> CommonButtons.onClick (fun _ -> TalgilTriggerFormMsg.CreateTrigger data |> dispatch)
            | None -> Button.Disabled true
        ] [ str "Erstellen" ]
    ]